// ============================================================================
// Core Types
// ============================================================================

export type DutyType = string; // Dynamic - comes from API (ID or identifier)

export const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

export type DayOfWeek = typeof DAYS_OF_WEEK[number];

export type DutyTimePeriod = {
  start_time: string; // Format: "HH:mm"
  end_time: string; // Format: "HH:mm"
  required_people: number;
};

export type DutyConfiguration = {
  duty_type: DutyType;
  duty_name?: string; // Display name from API
  is_enabled: boolean;
  time_periods: DutyTimePeriod[] | null;
  max_people: number | null;
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Gets the display label for a duty
 * @param duty - The duty configuration
 * @returns The display name
 */
export const getDutyLabel = (duty: DutyConfiguration): string => {
  return duty.duty_name || duty.duty_type;
};

/**
 * Checks if a duty is configured (has time periods or max people set)
 * @param duty - The duty configuration
 * @returns True if configured
 */
export const isDutyConfigured = (duty: DutyConfiguration): boolean => {
  return duty.is_enabled && (
    (duty.time_periods !== null && duty.time_periods.length > 0) ||
    (duty.max_people !== null && duty.max_people > 0)
  );
};

/**
 * Formats time periods for display
 * @param duty - The duty configuration
 * @returns Formatted time periods string or "--"
 */
export const formatDutyTimePeriods = (duty: DutyConfiguration): string => {
  if (!duty.is_enabled || !duty.time_periods || duty.time_periods.length === 0) {
    return '--';
  }
  return duty.time_periods
    .map(period => `${period.start_time} - ${period.end_time}`)
    .join('\n');
};

/**
 * Formats required people count for display
 * @param duty - The duty configuration
 * @returns Formatted required people string or "--"
 */
export const formatDutyRequiredPeople = (duty: DutyConfiguration): string => {
  if (!duty.is_enabled || !duty.time_periods || duty.time_periods.length === 0) {
    return '--';
  }
  return duty.time_periods
    .map(period => period.required_people?.toString() || '--')
    .join('\n');
};

/**
 * Formats max people count for display
 * @param duty - The duty configuration
 * @returns Formatted max people string or "--"
 */
export const formatDutyMaxPeople = (duty: DutyConfiguration): string => {
  if (!duty.is_enabled || duty.max_people === null || duty.max_people === undefined || duty.max_people === 0) {
    return '--';
  }
  return duty.max_people.toString();
};

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Type guard to check if a string is a valid DayOfWeek
 * @param day - The string to check
 * @returns True if the string is a valid DayOfWeek
 */
export const isValidDayOfWeek = (day: string): day is DayOfWeek => {
  return DAYS_OF_WEEK.includes(day as DayOfWeek);
};

/**
 * Type guard to check if an object is a valid DutyConfiguration
 * @param obj - The object to check
 * @returns True if the object is a valid DutyConfiguration
 */
export const isValidDutyConfiguration = (obj: unknown): obj is DutyConfiguration => {
  if (!obj || typeof obj !== 'object') return false;
  const duty = obj as Record<string, unknown>;
  return (
    typeof duty.duty_type === 'string' &&
    typeof duty.is_enabled === 'boolean' &&
    (duty.time_periods === null || Array.isArray(duty.time_periods)) &&
    (duty.max_people === null || typeof duty.max_people === 'number')
  );
};
